import React, { useCallback, useEffect, useRef, useState } from 'react'

import IconCalendar from '../icon/IconCalendar'
import { validateWithFormat } from './dateTimePickersUtils'
import { Box, InputGroup, InputRightElement } from 'front-end/src/components'
import Input from '../form/input/Input'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'

interface DatePickerInputProps {
  openCalendar: () => void
  disablePastDays?: boolean
  datePickerDateValue: string
  [key: string]: any
}

const DatePickerInput = ({
  openCalendar,
  value,
  onChange,
  disablePastDays = false,
  datePickerDateValue,
  ...props
}: DatePickerInputProps) => {
  const [localValue, setLocalValue] = useState(value)
  const wrapperRef = useRef(null)
  const isUpdated = value !== localValue
  const isLocalValid = validateWithFormat(localValue, disablePastDays)
  const { theme } = useLooopTheme()

  const updateValue = () => {
    // will trigger date value update
    onChange({ target: { value: localValue } })
  }

  const handleChange = (value: string) => {
    setLocalValue(value)
  }

  const checkSync = useCallback(() => {
    if (isLocalValid) {
      if (isUpdated) updateValue()
    } else {
      setLocalValue(value)
    }
  }, [value, localValue])

  useEffect(() => {
    if (isLocalValid && isUpdated) {
      updateValue()
    }
  }, [localValue])

  useEffect(() => {
    if (isUpdated) {
      setLocalValue(value)
    }
  }, [value])

  useEffect(() => {
    // we should clear input value with outer date picker state
    if (!datePickerDateValue && datePickerDateValue !== value) {
      setLocalValue('')
    }
  }, [datePickerDateValue])

  return (
    <InputGroup ref={wrapperRef}>
      <Input
        {...props}
        onChange={handleChange}
        onBlur={checkSync}
        value={localValue}
        className={props.className}
        fullWidth
      />
      <InputRightElement w="auto" onClick={openCalendar}>
        <Box
          w="32px"
          pr="4px"
          d="flex"
          alignItems="center"
          justifyContent="center"
        >
          <IconCalendar color={theme.colors.grey[500]} />
        </Box>
      </InputRightElement>
    </InputGroup>
  )
}

export default DatePickerInput
